import type { Action, Nft } from '@0xtorch/core'
import type { Chain } from '../../../../chain'
import type {
  Erc20TokenTransfer,
  Erc721TokenTransfer,
  Erc1155TokenTransfer,
  LowerHex,
  TransactionDecoded,
} from '../../../../types'
import { getAllValue } from './allValue'
import { checkTransferIsMatchErc20Pattern } from './erc20Pattern'
import { checkTransferIsMatchErc721Pattern } from './erc721Pattern'
import { checkTransferIsMatchErc1155Pattern } from './erc1155Pattern'
import { generateTransfers } from './transferGenerator'
import type { Generator } from './type'

type GenerateActionsParameters = {
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
  readonly generator: Generator
  readonly source: string
}

export const generateActions = ({
  chain,
  transaction,
  relatedAddresses,
  generator,
  source,
}: GenerateActionsParameters): readonly Action[] => {
  // target 取得
  if (generator.target !== undefined) {
    const actions: Action[] = []
    switch (generator.target.type) {
      case 'erc20': {
        for (const transfer of transaction.erc20Transfers) {
          if (
            !checkTransferIsMatchErc20Pattern({
              transfer,
              schema: generator.target,
              transaction,
              relatedAddresses,
            })
          ) {
            continue
          }
          actions.push(
            generateActionsWithTarget({
              chain,
              transaction,
              relatedAddresses,
              generator,
              source,
              erc20Target: transfer,
            }),
          )
        }
        break
      }
      case 'erc721': {
        for (const transfer of transaction.erc721Transfers) {
          if (
            !checkTransferIsMatchErc721Pattern({
              transfer,
              schema: generator.target,
              transaction,
              relatedAddresses,
            })
          ) {
            continue
          }
          actions.push(
            generateActionsWithTarget({
              chain,
              transaction,
              relatedAddresses,
              generator,
              source,
              erc721Target: transfer,
            }),
          )
        }
        break
      }
      case 'erc1155': {
        for (const transfer of transaction.erc1155Transfers) {
          if (
            !checkTransferIsMatchErc1155Pattern({
              transfer,
              schema: generator.target,
              transaction,
              relatedAddresses,
            })
          ) {
            continue
          }
          actions.push(
            generateActionsWithTarget({
              chain,
              transaction,
              relatedAddresses,
              generator,
              source,
              erc1155Target: transfer,
            }),
          )
        }
        break
      }
      default: {
        throw new Error(
          `Unknown target type: ${generator.target satisfies never}`,
        )
      }
    }
    return actions
  }

  // target 未指定の場合は単一 action 生成
  return [
    generateActionsWithTarget({
      chain,
      transaction,
      relatedAddresses,
      generator,
      source,
    }),
  ]
}

const generateActionsWithTarget = ({
  chain,
  transaction,
  relatedAddresses,
  generator,
  source,
  erc20Target,
  erc721Target,
  erc1155Target,
}: {
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
  readonly generator: Generator
  readonly source: string
  readonly erc20Target?: Erc20TokenTransfer
  readonly erc721Target?: Erc721TokenTransfer
  readonly erc1155Target?: Erc1155TokenTransfer
}): Action => {
  return {
    source,
    order: 0,
    type: generator.type,
    timestamp: transaction.timestamp,
    evidence: 'contract',
    comment:
      generator.comment === undefined
        ? undefined
        : getAllValue({
            schema: generator.comment,
            transaction,
            relatedAddresses,
          }),
    app: transaction.to?.app ?? transaction.from.app,
    transfers: generator.transfers.flatMap((schema) =>
      generateTransfers({
        schema,
        chain,
        transaction,
        relatedAddresses,
        erc20Target,
        erc721Target,
        erc1155Target,
      }),
    ),
    crossId:
      generator.crossId === undefined
        ? undefined
        : getAllValue({
            schema: generator.crossId,
            transaction,
            relatedAddresses,
          }),
    crossType: generator.crossType,
    loanId:
      generator.loanId === undefined
        ? undefined
        : getAllValue({
            schema: generator.loanId,
            transaction,
            relatedAddresses,
          }),
    target: generateValueTarget({
      transaction,
      relatedAddresses,
      schema: generator.valueTarget,
    }),
  }
}

const generateValueTarget = ({
  transaction,
  relatedAddresses,
  schema,
}: {
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
  readonly schema: Generator['valueTarget']
}): Nft | undefined => {
  if (schema === undefined) {
    return undefined
  }

  let currentIndex = -1
  switch (schema.type) {
    case 'erc721': {
      for (const transfer of transaction.erc721Transfers) {
        const matched = checkTransferIsMatchErc721Pattern({
          transfer,
          schema: schema,
          transaction,
          relatedAddresses,
        })
        if (matched) {
          currentIndex += 1
          if (currentIndex === schema.index) {
            return transfer.token
          }
        }
      }
      break
    }
    case 'erc1155': {
      for (const transfer of transaction.erc1155Transfers) {
        const matched = checkTransferIsMatchErc1155Pattern({
          transfer,
          schema: schema,
          transaction,
          relatedAddresses,
        })
        if (matched) {
          currentIndex += 1
          if (currentIndex === schema.index) {
            return transfer.token
          }
        }
      }
      break
    }
    default: {
      throw new Error(`Unknown value target type: ${schema satisfies never}`)
    }
  }
  throw new Error('Matched ERC721 or ERC1155 transfer is not found')
}
